import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ShareLinksApi } from '@/shared/api/shareLinks'
import { useAuth } from '@/modules/auth'
import { Button, Card, CardContent, Skeleton, ContentSkeleton, Logo } from '@/ui/components'
import { TripViewPage } from './TripViewPage'
import { AlertCircle } from '@/ui/icons'
import { AppHandoffBanner } from '@/components/AppHandoffBanner'

export default function ShareLinkView() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()
  const [isValidating, setIsValidating] = useState(true)
  const [accessContext, setAccessContext] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const validateAndLoad = async () => {
      if (!token) {
        setError('Invalid share link')
        setIsValidating(false)
        return
      }

      try {
        const api = new ShareLinksApi(getAccessToken)
        const validation = await api.validateShareLink(token)

        if (!validation.valid) {
          setError('This share link has expired or been revoked')
          setIsValidating(false)
          return
        }

        const perms = validation.permissions
        setAccessContext({
          source: 'private_link',
          role: 'viewer',
          permissions: {
            viewItinerary: perms?.viewItinerary ?? false,
            viewCampsiteCoordinates: perms?.viewCampsiteCoordinates ?? false,
            viewCrew: perms?.viewCrew ?? false,
            viewAnnouncements:
              perms?.viewAnnouncements ?? (perms as { viewNotes?: boolean })?.viewNotes ?? false,
            viewRadioChannel: perms?.viewRadioChannel ?? false,
            viewMeetupLocation: perms?.viewMeetupLocation ?? false,
            canEdit: false,
            canJoin: validation.allowJoin ?? false
          },
          tripId: validation.tripId,
          expiresAt: validation.expiresAt,
          shareToken: token // Pass the token so TripViewPage can fetch data and join
        })
      } catch (err) {
        setError('Failed to validate share link')
      } finally {
        setIsValidating(false)
      }
    }

    validateAndLoad()
  }, [token, getAccessToken])

  if (isValidating) {
    return (
      <div className="min-h-screen bg-earth-50">
        {/* Hero-shaped placeholder so the layout doesn't jump when the trip loads */}
        <Skeleton className="h-56 w-full rounded-none sm:h-72" />
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="-mt-6 space-y-4">
            <Skeleton className="h-40 w-full rounded-2xl" />
            <ContentSkeleton blocks={2} />
          </div>
        </div>
      </div>
    )
  }

  if (error || !accessContext) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-earth-50 px-4 py-10">
        <Card className="w-full max-w-md rounded-2xl shadow-lg">
          <CardContent className="flex flex-col items-center p-6 text-center sm:p-8">
            <Logo size="sm" to="/" className="mb-6" />
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">Link expired</h1>
            <p className="mb-1 text-sm text-muted-foreground">{error}</p>
            <p className="mb-6 text-sm text-muted-foreground">
              This link may have expired or been revoked by the trip owner. Please request a new
              link.
            </p>
            <Button onClick={() => navigate('/')} className="w-full sm:w-auto">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <>
      {token && <AppHandoffBanner token={token} />}
      <TripViewPage accessContext={accessContext} />
    </>
  )
}
